import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { PasswordInput } from '../components/Auth/PasswordInput'
import { User, Key, ArrowLeft, Save, Mail, AlertCircle, CheckCircle, Loader2 } from 'lucide-react'
import type { PasswordChangeData, ProfileUpdateData } from '../types/auth'

export const AccountSettings: React.FC = () => {
  const { user, updateProfile, changePassword, loading, error, clearError } = useAuth()
  const navigate = useNavigate()

  // Form states
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: ''
  })
  
  const [passwordData, setPasswordData] = useState<PasswordChangeData>({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: ''
  })
  
  // UI states
  const [profileErrors, setProfileErrors] = useState<{[key: string]: string}>({})
  const [passwordErrors, setPasswordErrors] = useState<{[key: string]: string}>({})
  const [profileLoading, setProfileLoading] = useState(false)
  const [passwordLoading, setPasswordLoading] = useState(false)
  const [resetEmailLoading, setResetEmailLoading] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [resetEmailSent, setResetEmailSent] = useState(false)

  // Initialize form data from user
  useEffect(() => {
    if (user) {
      const fullName = user.user_metadata?.full_name || ''
      const [firstName = '', ...lastNameParts] = fullName.split(' ')
      const lastName = lastNameParts.join(' ')
      
      setProfileData({
        firstName,
        lastName,
        email: user.email || ''
      })
    }
  }, [user])

  // Clear messages after delay
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 5000)
      return () => clearTimeout(timer)
    }
  }, [successMessage])

  // Clear auth error when component mounts or user types
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => clearError(), 5000)
      return () => clearTimeout(timer)
    }
  }, [error, clearError])

  // Handle input changes
  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setProfileData(prev => ({ ...prev, [name]: value }))
    
    // Clear field error
    if (profileErrors[name]) {
      setProfileErrors(prev => ({ ...prev, [name]: '' }))
    }
    
    // Clear auth error
    if (error) {
      clearError()
    }
  }

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setPasswordData(prev => ({ ...prev, [name]: value }))
    
    // Clear field error
    if (passwordErrors[name]) {
      setPasswordErrors(prev => ({ ...prev, [name]: '' }))
    }
    
    // Clear auth error
    if (error) {
      clearError()
    }
  }

  // Validation functions
  const validateProfile = (): boolean => {
    const errors: {[key: string]: string} = {}

    if (!profileData.firstName.trim()) {
      errors.firstName = 'First name is required'
    } else if (profileData.firstName.length < 2) {
      errors.firstName = 'First name must be at least 2 characters'
    }

    if (!profileData.lastName.trim()) {
      errors.lastName = 'Last name is required'
    } else if (profileData.lastName.length < 2) {
      errors.lastName = 'Last name must be at least 2 characters'
    }

    if (!profileData.email.trim()) {
      errors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileData.email)) {
      errors.email = 'Please enter a valid email address'
    }

    setProfileErrors(errors)
    return Object.keys(errors).length === 0
  }

  const validatePassword = (): boolean => {
    const errors: {[key: string]: string} = {}

    if (!passwordData.currentPassword.trim()) {
      errors.currentPassword = 'Current password is required'
    }

    if (!passwordData.newPassword.trim()) {
      errors.newPassword = 'New password is required'
    } else if (passwordData.newPassword.length < 8) {
      errors.newPassword = 'Password must be at least 8 characters'
    }

    if (!passwordData.confirmNewPassword.trim()) {
      errors.confirmNewPassword = 'Please confirm your new password'
    } else if (passwordData.newPassword !== passwordData.confirmNewPassword) {
      errors.confirmNewPassword = 'Passwords do not match'
    }

    setPasswordErrors(errors)
    return Object.keys(errors).length === 0
  }

  // Form submission handlers
  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateProfile()) {
      return
    }

    setProfileLoading(true)
    
    try {
      const profileUpdateData: ProfileUpdateData = {
        email: profileData.email,
        full_name: `${profileData.firstName} ${profileData.lastName}`.trim()
      }

      const { error } = await updateProfile(profileUpdateData)
      
      if (error) {
        console.error('Profile update error:', error)
      } else {
        setSuccessMessage('Profile updated successfully!')
      }
    } catch (err) {
      console.error('Profile update exception:', err)
    } finally {
      setProfileLoading(false)
    }
  }

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validatePassword()) {
      return
    }

    setPasswordLoading(true)
    
    try {
      const { error } = await changePassword(passwordData)
      
      if (error) {
        console.error('Password change error:', error)
      } else {
        setSuccessMessage('Password changed successfully!')
        // Clear password form
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmNewPassword: ''
        })
      }
    } catch (err) {
      console.error('Password change exception:', err)
    } finally {
      setPasswordLoading(false)
    }
  }

  const handlePasswordReset = async () => {
    if (!user?.email) return

    setResetEmailLoading(true)

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
        redirectTo: `${window.location.origin}/reset-password`
      })

      if (error) {
        console.error('Password reset error:', error)
      } else {
        setResetEmailSent(true)
        setSuccessMessage('Password reset email sent! Check your inbox.')
      }
    } catch (err) {
      console.error('Password reset exception:', err)
    } finally {
      setResetEmailLoading(false)
    }
  }

  // Don't render if not authenticated
  if (!user) {
    navigate('/chat')
    return null
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-primary via-dark-secondary to-dark-tertiary">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-br from-primary-600/10 to-transparent rounded-full blur-3xl" />
        <div className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-to-tl from-primary-500/10 to-transparent rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header */}
        <div className="bg-dark-secondary border-b border-dark-tertiary p-4">
          <div className="max-w-4xl mx-auto flex items-center space-x-4">
            <button
              onClick={() => navigate('/chat')}
              className="flex items-center space-x-2 px-3 py-2 text-light-secondary hover:text-light-primary transition-colors rounded-lg hover:bg-dark-tertiary/30"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Chat</span>
            </button>
            
            <div>
              <h1 className="text-xl font-semibold text-light-primary">Account Settings</h1>
              <p className="text-sm text-light-tertiary">Manage your personal information and security</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-6">
          <div className="max-w-2xl mx-auto space-y-8">
            
            {/* Success Message */}
            {successMessage && (
              <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                <p className="text-green-400 text-sm">{successMessage}</p>
              </div>
            )}

            {/* Auth Error */}
            {error && (
              <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center space-x-3">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            {/* Personal Information Section */}
            <div className="bg-gradient-to-br from-dark-secondary/80 to-dark-tertiary/60 backdrop-blur-lg border border-dark-tertiary/30 rounded-2xl p-8 shadow-xl">
              <div className="flex items-center space-x-3 mb-6">
                <User className="w-6 h-6 text-primary-400" />
                <h2 className="text-xl font-semibold text-light-primary">Personal Information</h2>
              </div>

              <form onSubmit={handleProfileSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* First Name */}
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-light-secondary mb-2">
                      First Name
                    </label>
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      value={profileData.firstName}
                      onChange={handleProfileChange}
                      placeholder="Enter your first name"
                      className={`
                        w-full px-4 py-3
                        bg-dark-secondary/50 
                        border border-dark-tertiary/30 
                        rounded-lg 
                        text-light-primary 
                        placeholder-light-tertiary 
                        backdrop-blur-sm
                        focus:outline-none 
                        focus:ring-2 
                        focus:ring-primary-500/50 
                        focus:border-primary-500
                        transition-all duration-200
                        ${profileErrors.firstName ? 'border-red-500 focus:ring-red-500/50 focus:border-red-500' : ''}
                      `}
                    />
                    {profileErrors.firstName && (
                      <p className="mt-1 text-sm text-red-400">{profileErrors.firstName}</p>
                    )}
                  </div>

                  {/* Last Name */}
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-light-secondary mb-2">
                      Last Name
                    </label>
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      value={profileData.lastName}
                      onChange={handleProfileChange}
                      placeholder="Enter your last name"
                      className={`
                        w-full px-4 py-3
                        bg-dark-secondary/50 
                        border border-dark-tertiary/30 
                        rounded-lg 
                        text-light-primary 
                        placeholder-light-tertiary 
                        backdrop-blur-sm
                        focus:outline-none 
                        focus:ring-2 
                        focus:ring-primary-500/50 
                        focus:border-primary-500
                        transition-all duration-200
                        ${profileErrors.lastName ? 'border-red-500 focus:ring-red-500/50 focus:border-red-500' : ''}
                      `}
                    />
                    {profileErrors.lastName && (
                      <p className="mt-1 text-sm text-red-400">{profileErrors.lastName}</p>
                    )}
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-light-secondary mb-2">
                    Email Address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={profileData.email}
                    onChange={handleProfileChange}
                    placeholder="Enter your email address"
                    className={`
                      w-full px-4 py-3
                      bg-dark-secondary/50 
                      border border-dark-tertiary/30 
                      rounded-lg 
                      text-light-primary 
                      placeholder-light-tertiary 
                      backdrop-blur-sm
                      focus:outline-none 
                      focus:ring-2 
                      focus:ring-primary-500/50 
                      focus:border-primary-500
                      transition-all duration-200
                      ${profileErrors.email ? 'border-red-500 focus:ring-red-500/50 focus:border-red-500' : ''}
                    `}
                  />
                  {profileErrors.email && (
                    <p className="mt-1 text-sm text-red-400">{profileErrors.email}</p>
                  )}
                </div>

                {/* Save Button */}
                <button
                  type="submit"
                  disabled={profileLoading || loading}
                  className="
                    flex items-center justify-center space-x-2 px-6 py-3
                    bg-primary-500 hover:bg-primary-600 disabled:bg-dark-tertiary
                    text-white rounded-lg transition-all duration-200
                    disabled:cursor-not-allowed disabled:text-light-tertiary
                    shadow-lg hover:shadow-xl focus:ring-2 focus:ring-primary-500/50
                  "
                >
                  {profileLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  <span>{profileLoading ? 'Saving...' : 'Save Changes'}</span>
                </button>
              </form>
            </div>

            {/* Password Management Section */}
            <div className="bg-gradient-to-br from-dark-secondary/80 to-dark-tertiary/60 backdrop-blur-lg border border-dark-tertiary/30 rounded-2xl p-8 shadow-xl">
              <div className="flex items-center space-x-3 mb-6">
                <Key className="w-6 h-6 text-primary-400" />
                <h2 className="text-xl font-semibold text-light-primary">Password Management</h2>
              </div>

              <form onSubmit={handlePasswordSubmit} className="space-y-6">
                {/* Current Password */}
                <div>
                  <label htmlFor="currentPassword" className="block text-sm font-medium text-light-secondary mb-2">
                    Current Password
                  </label>
                  <PasswordInput
                    id="currentPassword"
                    name="currentPassword"
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    placeholder="Enter your current password"
                    autoComplete="current-password"
                    error={!!passwordErrors.currentPassword}
                  />
                  {passwordErrors.currentPassword && (
                    <p className="mt-1 text-sm text-red-400">{passwordErrors.currentPassword}</p>
                  )}
                </div>

                {/* New Password */}
                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium text-light-secondary mb-2">
                    New Password
                  </label>
                  <PasswordInput
                    id="newPassword"
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    placeholder="Enter your new password"
                    autoComplete="new-password"
                    error={!!passwordErrors.newPassword}
                  />
                  {passwordErrors.newPassword && (
                    <p className="mt-1 text-sm text-red-400">{passwordErrors.newPassword}</p>
                  )}
                  <p className="mt-1 text-xs text-light-tertiary">Must be at least 8 characters long</p>
                </div>

                {/* Confirm New Password */}
                <div>
                  <label htmlFor="confirmNewPassword" className="block text-sm font-medium text-light-secondary mb-2">
                    Confirm New Password
                  </label>
                  <PasswordInput
                    id="confirmNewPassword"
                    name="confirmNewPassword"
                    value={passwordData.confirmNewPassword}
                    onChange={handlePasswordChange}
                    placeholder="Confirm your new password"
                    autoComplete="new-password"
                    error={!!passwordErrors.confirmNewPassword}
                  />
                  {passwordErrors.confirmNewPassword && (
                    <p className="mt-1 text-sm text-red-400">{passwordErrors.confirmNewPassword}</p>
                  )}
                </div>

                {/* Change Password Button */}
                <button
                  type="submit"
                  disabled={passwordLoading || loading}
                  className="
                    flex items-center justify-center space-x-2 px-6 py-3
                    bg-primary-500 hover:bg-primary-600 disabled:bg-dark-tertiary
                    text-white rounded-lg transition-all duration-200
                    disabled:cursor-not-allowed disabled:text-light-tertiary
                    shadow-lg hover:shadow-xl focus:ring-2 focus:ring-primary-500/50
                  "
                >
                  {passwordLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Key className="w-4 h-4" />
                  )}
                  <span>{passwordLoading ? 'Changing Password...' : 'Change Password'}</span>
                </button>

                {/* Password Reset Section */}
                <div className="border-t border-dark-tertiary/30 pt-6 mt-6">
                  <h3 className="text-lg font-medium text-light-primary mb-3">Can't Remember Your Password?</h3>
                  <p className="text-sm text-light-tertiary mb-4">
                    If you can't remember your current password, we can send you a password reset email.
                  </p>
                  <button
                    type="button"
                    onClick={handlePasswordReset}
                    disabled={resetEmailLoading || resetEmailSent}
                    className="
                      flex items-center justify-center space-x-2 px-4 py-2
                      bg-dark-tertiary hover:bg-dark-quaternary disabled:bg-dark-tertiary/50
                      text-light-secondary hover:text-light-primary disabled:text-light-tertiary
                      border border-dark-tertiary/50 rounded-lg transition-all duration-200
                      disabled:cursor-not-allowed focus:ring-2 focus:ring-primary-500/50
                    "
                  >
                    {resetEmailLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Mail className="w-4 h-4" />
                    )}
                    <span>
                      {resetEmailLoading ? 'Sending...' : 
                       resetEmailSent ? 'Email Sent!' : 
                       'Send Password Reset Email'}
                    </span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}